import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

const TRIAL_DAYS = 30;
const TRIAL_EVENT = "teamlio:workflow-trial-change";

const storageKey = (userId: string, field: "started" | "quit") =>
  `teamlio:workflow-trial:${userId}:${field}`;

export function useWorkflowTrial() {
  const { user } = useAuth();
  const limited = user?.subscription?.workspace_mode === "limited";
  const userId = user?.id || "anonymous";
  const [revision, refresh] = useState(0);

  useEffect(() => {
    const update = () => refresh((value) => value + 1);
    window.addEventListener(TRIAL_EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(TRIAL_EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, []);

  const state = useMemo(() => {
    if (!limited) {
      return { limited: false, expired: false, quit: false, daysLeft: 0 };
    }

    const startedKey = storageKey(userId, "started");
    let startedAt = Number(localStorage.getItem(startedKey));
    if (!startedAt) {
      startedAt = Date.now();
      localStorage.setItem(startedKey, String(startedAt));
    }

    const subscriptionEnd = user?.subscription?.trial_end
      ? new Date(user.subscription.trial_end).getTime()
      : 0;
    const endsAt =
      subscriptionEnd ||
      startedAt + TRIAL_DAYS * 24 * 60 * 60 * 1000;
    const remainingMs = endsAt - Date.now();

    return {
      limited: true,
      expired: remainingMs <= 0,
      quit: localStorage.getItem(storageKey(userId, "quit")) === "true",
      daysLeft: Math.max(0, Math.ceil(remainingMs / (24 * 60 * 60 * 1000))),
    };
  }, [limited, userId, user?.subscription?.trial_end, revision]);

  const quitWorkflow = useCallback(() => {
    localStorage.setItem(storageKey(userId, "quit"), "true");
    window.dispatchEvent(new Event(TRIAL_EVENT));
  }, [userId]);

  return {
    ...state,
    visible: !state.limited || !state.quit,
    quitWorkflow,
  };
}
